import { cn } from '@/lib/cn';

export type TabsVariant = 'underline' | 'segmented' | 'pills';

export interface TabItem {
  id: string;
  label: string;
  badge?: number;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  variant?: TabsVariant;
  className?: string;
  'aria-label'?: string;
}

export function Tabs({
  tabs,
  activeId,
  onChange,
  variant = 'underline',
  className,
  'aria-label': ariaLabel,
}: TabsProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex flex-wrap gap-1',
        variant === 'underline' && 'border-b border-[var(--border-subtle)]',
        variant === 'segmented' &&
          'rounded-lg border border-[var(--border-visible)] bg-[var(--bg-card)] p-1',
        variant === 'pills' && 'gap-2',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative px-3 py-2 text-sm font-medium transition-colors',
              tab.disabled && 'cursor-not-allowed opacity-50',
              variant === 'underline' &&
                cn(
                  'tab-btn border-b-2 border-transparent text-secondary hover:text-[var(--accent)]',
                  isActive && 'border-[var(--accent)] text-[var(--accent)]',
                ),
              variant === 'segmented' &&
                cn(
                  'rounded-md text-secondary hover:text-[var(--text-primary)]',
                  isActive && 'bg-[var(--accent-bg)] text-[var(--accent)]',
                ),
              variant === 'pills' &&
                cn(
                  'rounded-full border border-[var(--border-visible)] text-secondary hover:bg-[var(--bg-card-hover)]',
                  isActive && 'border-[var(--accent)] bg-[var(--accent-bg)] text-[var(--accent)]',
                ),
            )}
          >
            <span className="inline-flex items-center gap-2">
              {tab.label}
              {tab.badge !== undefined ? (
                <span className="rounded-full bg-[var(--accent-bg)] px-1.5 py-0.5 text-xs text-[var(--accent)]">
                  {tab.badge}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
